<script>
export default {
  name: "DidoPagination",
};
</script>

<script setup>
import DidoDatasetCard from "./DatasetCard.vue";
import DidoDatafileCard from "./DatafileCard.vue";
import { ref, watch, computed } from "vue";
import api from "../api";
import { createCatalogFilter, pause } from "../utils";
import { formatMinDate, formatMaxDate } from "../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  paginationFilter: {
    type: [Object, String],
    required: false,
    default: () => {},
  },
});

// define events emitted
const emit = defineEmits([
  "click-card",
  "label-selection-on-card",
  "bad-configuration",
  "new-page",
  "init",
]);

// define Reactive Data
const initialization = ref(true);
const initializationError = ref(false);

const total = ref(0);
const items = ref([]);
const currentPage = ref(1);
const lastPage = ref(1);
const firstPage = ref(1);

const paginationClicked = ref(false);

// define ref for dom reference of pagination control
const paginationControl = ref(null);

// define computed datas
const filter = computed(() =>
  createCatalogFilter(
    typeof props.paginationFilter === "string"
      ? JSON.parse(props.paginationFilter)
      : props.paginationFilter,
  ),
);
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.paginationFilter) return false;
  return true;
});

// define watchers
watch(
  () => [props.apiUrl, props.paginationFilter],
  () => init(),
  { deep: true },
);
watch(
  () => paginationClicked.value,
  async () => {
    if (!paginationClicked.value) {
      await pause(1);
      paginationControl.value?.$el.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    }
  },
);

// define computed data for pagination params
const paginationParams = computed(() => {
  let params = {
    page: filter.value.pagination,
    pageSize: 10,
    orderBy: `${filter.value.order.sort === "desc" ? "-" : ""}${
      filter.value.order.field
    }`,
  };
  if (filter.value.text) params.text = filter.value.text;
  if (filter.value.lastModified.min)
    params.minLastModified = formatMinDate(filter.value.lastModified.min);
  if (filter.value.lastModified.max)
    params.maxLastModified = formatMaxDate(filter.value.lastModified.max);
  if (filter.value.temporalCoverage.from)
    params.temporalCoverageStart = formatMinDate(
      filter.value.temporalCoverage.from,
    );
  if (filter.value.temporalCoverage.to)
    params.temporalCoverageEnd = formatMaxDate(
      filter.value.temporalCoverage.to,
    );
  if (filter.value.topics.length) params.topics = filter.value.topics.join(",");
  if (filter.value.tags.length) params.tags = filter.value.tags.join(",");
  if (filter.value.licenses.length)
    params.licenses = filter.value.licenses.join(",");
  if (filter.value.zones.length) params.zones = filter.value.zones.join(",");
  if (filter.value.granularities.length)
    params.granularities = filter.value.granularities.join(",");
  if (filter.value.frequencies.length)
    params.frequencies = filter.value.frequencies.join(",");
  return params;
});

// method for initialization of widget
const init = async () => {
  // if widget bad configured
  if (!configured.value) {
    initialization.value = false;
    initializationError.value = true;
    emit("bad-configuration");
    return;
  }

  // flag initialization in progress
  initialization.value = true;

  // reset initialization error
  initializationError.value = false;

  getPage()
    .then(() => {
      initialization.value = false;
    })
    .catch(() => {
      initialization.value = false;
      initializationError.value = true;
    });
};
// method for getting a specific page
const getPage = async () => {
  // get results from api
  const apiResultPagination = await (filter.value.itemsType === "dataset"
    ? api.getDatasets(props.apiUrl, paginationParams.value)
    : api.getDatafiles(props.apiUrl, paginationParams.value));
  // if error with api results
  if (apiResultPagination.status !== "available") throw new Error();

  // push new page in items
  items.value = apiResultPagination.pagination.data;
  firstPage.value = 1;
  total.value = apiResultPagination.pagination.total;
  lastPage.value = Math.ceil(total.value / 10);
  currentPage.value = apiResultPagination.pagination.page;

  if (paginationClicked.value) paginationClicked.value = false;
};

// method when pagination is clicked
const clickPagination = (page) => {
  emit("new-page", page);
  paginationClicked.value = true;
};

// Init
if (filter.value) init();
</script>

<template>
  <div
    class="dido-widget-pagination relative-position col-xs-12 col-sm-7 col-md-8 col-lg-9 col-xl-9"
  >
    <!-- Bad configuration of widget -->
    <div v-if="!configured">
      <div>Impossible de charger le composant...</div>
    </div>
    <template v-else>
      <!-- Flexbox for Items of Pagination -->
      <div
        v-if="!initialization & !initializationError"
        class="row justify-between items-stretch content-center q-col-gutter-md"
      >
        <!-- Item: Dataset Card Component  -->
        <template v-if="filter.itemsType === 'dataset'">
          <DidoDatasetCard
            v-for="(item, index) in items"
            :key="index"
            :api-url="props.apiUrl"
            :dataset="item"
            class="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
            @click-card="
              emit('click-card', { type: 'dataset', datasetId: item.id })
            "
            @label-selection="
              emit('label-selection-on-card', {
                card: 'dataset',
                type: $event.type,
                value: $event.value,
              })
            "
          />
        </template>
        <!-- Item: Datafile Card Component  -->
        <template v-if="filter.itemsType === 'datafile'">
          <DidoDatafileCard
            v-for="(item, index) in items"
            :key="index"
            :api-url="props.apiUrl"
            :datafile="item"
            class="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6"
            @click-card="
              emit('click-card', { type: 'datafile', datafileRid: item.rid })
            "
            @label-selection="
              emit('label-selection-on-card', {
                card: 'datafile',
                type: $event.type,
                value: $event.value,
              })
            "
          />
        </template>
      </div>

      <!-- Pagination Navigation Control -->
      <div
        v-if="!initialization & !initializationError"
        class="row-all-center q-col-gutter-md q-mt-md"
      >
        <QPagination
          ref="paginationControl"
          v-model="currentPage"
          :min="firstPage"
          :max="lastPage"
          direction-links
          boundary-links
          color="secondary"
          unelevated
          @update:model-value="clickPagination"
        />
      </div>

      <div v-if="initialization" class="row justify-center q-my-md">
        <QSpinnerDots color="secondary" size="40px" />
      </div>
      <div
        v-if="initializationError"
        class="text-negative text-bold text-caption"
      >
        Erreur pendant la récupération des données !!!
      </div>
    </template>
  </div>
</template>
